<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Button, Modal } from '@ui'
import type { AnalystAvailability } from '@/api/analyst'

/**
 * The prompt-content contract in plain language (P4 §5.3, S0 §49b.2): what
 * goes to the model, what never does, and that each question is on the
 * institution's transparency record (S0 §45b.2).
 */
defineProps<{ open: boolean; availability: AnalystAvailability }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()

/** S0 §17.1: MIN_COHORT_DISPLAY_N. */
const MIN_COHORT_DISPLAY_N = 10

/** Every content class the engine sends (its transparency entry names the same ones). */
const SENT = ['question', 'function', 'results', 'definitions', 'readiness', 'knowledge'] as const
const NEVER = ['patients', 'members', 'clinical', 'small'] as const
</script>

<template>
  <Modal :model-value="open" :title="t('analyst.howItWorks.title')" size="md" type="content" no-icon @close="emit('close')">
    <template #content>
      <div class="contract" data-test="analyst-contract">
        <p class="contract__lead">{{ t('analyst.howItWorks.where') }}</p>

        <section>
          <h3 class="contract__heading">{{ t('analyst.howItWorks.sentTitle') }}</h3>
          <ul class="contract__list">
            <li v-for="item in SENT" :key="item">{{ t(`analyst.howItWorks.sent.${item}`) }}</li>
          </ul>
        </section>

        <section>
          <h3 class="contract__heading">{{ t('analyst.howItWorks.neverTitle') }}</h3>
          <ul class="contract__list">
            <li v-for="item in NEVER" :key="item">
              {{ t(`analyst.howItWorks.never.${item}`, { minimum: MIN_COHORT_DISPLAY_N }) }}
            </li>
          </ul>
        </section>

        <p class="contract__text">{{ t('analyst.howItWorks.numbers') }}</p>
        <p class="contract__text">{{ t('analyst.howItWorks.record') }}</p>
      </div>
    </template>
    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('analyst.panel.close') }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.contract {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-primary);
}

.contract__lead,
.contract__text {
  margin: 0;
}

.contract__text {
  color: var(--fg-secondary);
}

.contract__heading {
  margin: 0 0 var(--spacing-xs);
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  font-weight: var(--font-weight-semibold);
}

.contract__list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  margin: 0;
  padding-inline-start: var(--spacing-xl);
}
</style>
